import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import {
  Sigma, FlaskConical, Atom, Leaf, Hourglass, Globe, TrendingUp, Code2,
} from 'lucide-react';
import SEO from '../components/SEO';
import PubliekeNav from '../components/PubliekeNav';
import PubliekeFooter from '../components/PubliekeFooter';
import HOME_TEKSTEN from '../content/paginas/home';
import { alternatief, pad, vakPad } from '../content/site/paden';
import KennisbankKaart from '../components/kennisbank/KennisbankKaart';
import { nieuwsteEerst } from '../utils/kennisbank';
import './HomeHero.css';

// Inline vlag-componenten — geen library, geen emoji, render identiek op elk OS
const FlagGB = (props) => (
  <svg viewBox="0 0 60 30" width="34" height="24" {...props}>
    <clipPath id="t"><path d="M30,15 h30 v15 z v15 h-30 z h-30 v-15 z v-15 h30 z" /></clipPath>
    <rect width="60" height="30" fill="#012169" />
    <path d="M0,0 L60,30 M60,0 L0,30" stroke="#fff" strokeWidth="6" />
    <path d="M0,0 L60,30 M60,0 L0,30" clipPath="url(#t)" stroke="#C8102E" strokeWidth="4" />
    <path d="M30,0 V30 M0,15 H60" stroke="#fff" strokeWidth="10" />
    <path d="M30,0 V30 M0,15 H60" stroke="#C8102E" strokeWidth="6" />
  </svg>
);
const FlagNL = (props) => (
  <svg viewBox="0 0 9 6" width="34" height="22" {...props}>
    <rect width="9" height="2" fill="#AE1C28" />
    <rect y="2" width="9" height="2" fill="#fff" />
    <rect y="4" width="9" height="2" fill="#21468B" />
  </svg>
);
const FlagDE = (props) => (
  <svg viewBox="0 0 5 3" width="34" height="22" {...props}>
    <rect width="5" height="1" fill="#000" />
    <rect y="1" width="5" height="1" fill="#DD0000" />
    <rect y="2" width="5" height="1" fill="#FFCE00" />
  </svg>
);
const FlagFR = (props) => (
  <svg viewBox="0 0 3 2" width="34" height="22" {...props}>
    <rect width="1" height="2" fill="#0055A4" />
    <rect x="1" width="1" height="2" fill="#fff" />
    <rect x="2" width="1" height="2" fill="#EF4135" />
  </svg>
);

// Namen per taal: vakken.namen in content/paginas/home.js.
const VAKKEN = [
  { sleutel: 'wiskunde',       Icon: Sigma },
  { sleutel: 'scheikunde',     Icon: FlaskConical },
  { sleutel: 'natuurkunde',    Icon: Atom },
  { sleutel: 'biologie',       Icon: Leaf },
  { sleutel: 'engels',         Icon: FlagGB, isFlag: true },
  { sleutel: 'nederlands',     Icon: FlagNL, isFlag: true },
  { sleutel: 'duits',          Icon: FlagDE, isFlag: true },
  { sleutel: 'frans',          Icon: FlagFR, isFlag: true },
  { sleutel: 'geschiedenis',   Icon: Hourglass },
  { sleutel: 'aardrijkskunde', Icon: Globe },
  { sleutel: 'economie',       Icon: TrendingUp },
  { sleutel: 'informatica',    Icon: Code2 },
];

// Pagina's over digitaal toetsen per vak. Interne links helpen Google die pagina's
// te vinden; de URL's liggen vast (vakPad in content/site/paden.js).
const VAK_PAGINAS = ['wiskunde', 'talen', 'geschiedenis-en-aardrijkskunde'];

// Kleur en initialen bij de reviews (teksten in content/paginas/home.js).
const REVIEW_STIJL = [
  { initials: 'NL', color: '#0077b6' },
  { initials: 'LV', color: '#10b981' },
  { initials: 'MB', color: '#f59e0b' },
  { initials: 'ST', color: '#ef4444' },
  { initials: 'PH', color: '#8b5cf6' },
];

const PijlRechts = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M5 12h14" /><path d="M12 5l7 7-7 7" />
  </svg>
);

// Teksten in NL en EN: src/content/paginas/home.js. Route /en geeft lang="en".
function HomeHero({ lang = 'nl' }) {
  const t = HOME_TEKSTEN[lang] || HOME_TEKSTEN.nl;
  const [reviewIndex, setReviewIndex] = useState(0);
  const [statsVisible, setStatsVisible] = useState(false);
  const [counters, setCounters] = useState({ scholen: 0, studenten: 0, docenten: 0 });
  const [nieuwsteArtikelen, setNieuwsteArtikelen] = useState([]);
  const statsRef = useRef(null);
  const videoRef = useRef(null);

  // Unmute video op 50% volume na eerste klik ergens op de pagina
  useEffect(() => {
    const unmute = () => {
      if (videoRef.current) {
        videoRef.current.muted = false;
        videoRef.current.volume = 0.5;
      }
      document.removeEventListener('click', unmute);
    };
    document.addEventListener('click', unmute);
    return () => document.removeEventListener('click', unmute);
  }, []);

  // De drie nieuwste kennisbankartikelen. blogArticles is groot (ruim 100 kB
  // gezipt) en HomeHero zit in de main-chunk die elke docent en leerling
  // downloadt, dus artikelen en kaartstijl pas na de eerste paint ophalen.
  useEffect(() => {
    let actief = true;
    Promise.all([import('../data/blogArticles'), import('./Blog.css')])
      .then(([{ default: artikelen }]) => {
        if (actief) setNieuwsteArtikelen(nieuwsteEerst(artikelen).slice(0, 3));
      })
      // Lukt het laden niet, dan blijft de link naar de kennisbank staan.
      .catch(() => {});
    return () => { actief = false; };
  }, []);

  // Count-up animation when stats become visible
  useEffect(() => {
    const el = statsRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setStatsVisible(true); observer.disconnect(); } },
      { threshold: 0.5 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!statsVisible) return;
    const targets = { scholen: 3, studenten: 1000, docenten: 20 };
    const duration = 1800;
    const start = performance.now();
    const tick = (now) => {
      const t = Math.min((now - start) / duration, 1);
      const ease = 1 - Math.pow(1 - t, 3); // easeOutCubic
      setCounters({
        scholen: Math.round(targets.scholen * ease),
        studenten: Math.round(targets.studenten * ease),
        docenten: Math.round(targets.docenten * ease),
      });
      if (t < 1) requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  }, [statsVisible]);

  const reviews = t.reviews.map((r, i) => ({ ...r, ...REVIEW_STIJL[i] }));

  useEffect(() => {
    const timer = setInterval(() => {
      setReviewIndex(i => (i + 1) % reviews.length);
    }, 6000);
    return () => clearInterval(timer);
  }, [reviews.length]);

  return (
    <div className="hh-page">
      <SEO
        title={t.seo.title}
        description={t.seo.description}
        keywords={t.seo.keywords}
        canonical={pad('home', lang)}
        lang={lang}
        alternates={alternatief('home')}
        structuredData={[
          {
            '@context': 'https://schema.org',
            '@type': 'VideoObject',
            name: t.seo.videoNaam,
            description: t.seo.videoBeschrijving,
            thumbnailUrl: 'https://testwisely.nl/testwise-og.png',
            uploadDate: '2026-01-15T00:00:00+01:00',
            contentUrl: 'https://testwisely.nl/video/testwisely-demo.mp4',
            embedUrl: `https://testwisely.nl${pad('home', lang)}`,
            duration: 'PT2M30S',
            publisher: {
              '@type': 'Organization',
              name: 'TestWisely',
              logo: { '@type': 'ImageObject', url: 'https://testwisely.nl/tw-logo-04.png' }
            }
          },
          {
            '@context': 'https://schema.org',
            '@type': 'SoftwareApplication',
            name: 'TestWisely',
            applicationCategory: 'EducationalApplication',
            operatingSystem: 'Web, iOS, iPadOS, Windows',
            // Geen Offer: er is geen gratis versie. De prijs staat per school in
            // een offerte (Voorwaarden.js, artikel 3.4).
            aggregateRating: {
              '@type': 'AggregateRating',
              ratingValue: '4.8',
              reviewCount: '5',
              bestRating: '5',
              worstRating: '1'
            },
            review: reviews.map(r => ({
              '@type': 'Review',
              author: { '@type': 'Person', name: r.name },
              reviewBody: r.quote,
              reviewRating: { '@type': 'Rating', ratingValue: '5', bestRating: '5' }
            }))
          }
        ]}
      />

      {/* Decorative circles */}
      <div className="hh-circles">
        <div className="hh-circle hh-circle-1"></div>
        <div className="hh-circle hh-circle-2"></div>
        <div className="hh-circle hh-circle-3"></div>
      </div>

      <PubliekeNav lang={lang} alternatief={alternatief('home')} />

      {/* Hero Section */}
      <section className="hh-hero">
        <div className="hh-hero-content">
          <div className="hh-hero-left">
            <h1 className="hh-title">
              {t.hero.titel[0]}<br/>{t.hero.titel[1]}
            </h1>
            <h2 className="hh-subtitle">
              {t.hero.subtitel}
            </h2>
            <p className="hh-description">
              {t.hero.beschrijving}
            </p>
            <div className="hh-buttons">
              <Link to={pad('demo', lang)} className="hh-btn-primary">
                {t.hero.knopDemo}
              </Link>
              <Link to={pad('contact', lang)} className="hh-btn-outline">
                {t.hero.knopContact}
              </Link>
            </div>
          </div>

          <div className="hh-hero-right">
            <div className="hh-video-container">
              <video
                ref={videoRef}
                className="hh-video"
                autoPlay
                muted
                loop
                playsInline
                controls
                poster="/testwise-og.png"
              >
                <source src="/video/testwisely-demo.mp4" type="video/mp4" />
              </video>
            </div>
          </div>
        </div>

        {/* Stats - full width bottom of hero */}
        <div className="hh-hero-stats" ref={statsRef}>
          <div className="hh-stat">
            <span className="hh-stat-number">{counters.studenten}+</span>
            <span className="hh-stat-label">{t.stats.leerlingen}</span>
          </div>
          <div className="hh-stat-divider"></div>
          <div className="hh-stat">
            <span className="hh-stat-number">{counters.docenten}+</span>
            <span className="hh-stat-label">{t.stats.docenten}</span>
          </div>
          <div className="hh-stat-divider"></div>
          <div className="hh-stat">
            <span className="hh-stat-number">70%</span>
            <span className="hh-stat-label">{t.stats.nakijktijd}</span>
          </div>
        </div>
      </section>

      {/* Features Section - White */}
      <section className="hh-features">
        {/* Decorative logo circles - 3x3 TestWisely pattern */}
        <div className="hh-logo-circles hh-logo-circles-tl">
          <div className="hh-lc hh-lc-blue"></div>
          <div className="hh-lc hh-lc-blue"></div>
          <div className="hh-lc hh-lc-blue"></div>
          <div className="hh-lc hh-lc-white"></div>
          <div className="hh-lc hh-lc-lightblue"></div>
          <div className="hh-lc hh-lc-white"></div>
          <div className="hh-lc hh-lc-white"></div>
          <div className="hh-lc hh-lc-white"></div>
          <div className="hh-lc hh-lc-white"></div>
        </div>
        <div className="hh-logo-circles hh-logo-circles-br">
          <div className="hh-lc hh-lc-blue"></div>
          <div className="hh-lc hh-lc-blue"></div>
          <div className="hh-lc hh-lc-blue"></div>
          <div className="hh-lc hh-lc-white"></div>
          <div className="hh-lc hh-lc-lightblue"></div>
          <div className="hh-lc hh-lc-white"></div>
          <div className="hh-lc hh-lc-white"></div>
          <div className="hh-lc hh-lc-white"></div>
          <div className="hh-lc hh-lc-white"></div>
        </div>

        <div className="hh-features-header">
          <h2 className="hh-features-title">
            <span className="hh-blue">{t.features.titel.nadruk}</span>{t.features.titel.na}
          </h2>
          <h3 className="hh-features-subtitle">{t.features.subtitel}</h3>
          <p className="hh-features-desc">
            {t.features.beschrijving[0]}<br/>
            {t.features.beschrijving[1]}
          </p>
        </div>

        <div className="hh-features-grid">
          {/* Nakijken met AI */}
          <div className="hh-feature-card">
            <div className="hh-feature-icon">
              <img src="/icons/ai-brain.svg" alt="" className="hh-feature-svg" />
            </div>
            <h4 className="hh-feature-name">{t.features.ai.naam}</h4>
            <p className="hh-feature-text">
              {t.features.ai.voor}<span className="hh-blue-nadruk">{t.features.ai.nadruk}</span>{t.features.ai.na}
            </p>
          </div>

          {/* Nederlandse data opslag */}
          <div className="hh-feature-card">
            <div className="hh-feature-icon">
              <img src="/icons/database-locked.svg" alt="" className="hh-feature-svg" />
            </div>
            <h4 className="hh-feature-name">{t.features.opslag.naam}</h4>
            <p className="hh-feature-text">
              {t.features.opslag.voor}<span className="hh-blue-nadruk">{t.features.opslag.nadruk}</span>{t.features.opslag.na}<Link to={pad('subverwerkers', lang)}><span className="hh-blue-underline">{t.features.opslag.link}</span></Link>{t.features.opslag.slot}
            </p>
          </div>

          {/* Beveiligd afnemen */}
          <div className="hh-feature-card">
            <div className="hh-feature-icon">
              <img src="/icons/lock-computer.svg" alt="" className="hh-feature-svg" />
            </div>
            <h4 className="hh-feature-name">{t.features.beveiligd.naam}</h4>
            <p className="hh-feature-text">
              {t.features.beveiligd.voor}<span className="hh-blue-nadruk">{t.features.beveiligd.nadruk}</span>{t.features.beveiligd.na}
            </p>
          </div>

          {/* Koppeling met Zermelo */}
          <div className="hh-feature-card">
            <div className="hh-feature-icon">
              <img src="/icons/connect.svg" alt="" className="hh-feature-svg" />
            </div>
            <h4 className="hh-feature-name">{t.features.zermelo.naam}</h4>
            <p className="hh-feature-text">
              {t.features.zermelo.voor}<span className="hh-blue-nadruk">{t.features.zermelo.nadruk}</span>{t.features.zermelo.na}
            </p>
          </div>

          {/* Inloggen met schoolaccount */}
          <div className="hh-feature-card">
            <div className="hh-feature-icon">
              <img src="/icons/user-account.svg" alt="" className="hh-feature-svg" />
            </div>
            <h4 className="hh-feature-name">{t.features.sso.naam}</h4>
            <p className="hh-feature-text">
              {t.features.sso.voor}<span className="hh-blue-nadruk">{t.features.sso.nadruk}</span>{t.features.sso.na}
            </p>
          </div>

          {/* Alles overzichtelijk */}
          <div className="hh-feature-card">
            <div className="hh-feature-icon">
              <img src="/icons/arrow-shrink.svg" alt="" className="hh-feature-svg" />
            </div>
            <h4 className="hh-feature-name">{t.features.overzicht.naam}</h4>
            <p className="hh-feature-text">
              {t.features.overzicht.voor}<span className="hh-blue-nadruk">{t.features.overzicht.nadruk}</span>{t.features.overzicht.na}
            </p>
          </div>
        </div>

        <div className="hh-features-cta">
          <Link to={pad('demo', lang)} className="hh-btn-cta">
            {t.features.cta} <span>→</span>
          </Link>
        </div>
      </section>

      {/* Vertrouwd door, Logo's sectie */}
      <section className="hh-partners">
        <h2 className="hh-partners-title">
          <span className="hh-blue">{t.partners.titel.nadruk}</span>{t.partners.titel.na}
        </h2>
        <div className="hh-partners-logos">
          {/* Alleen Entree: daar is TestWisely op aangesloten. Edu-V (toetredingstraject)
              en ISO 27001 (certificaat van de hostingpartij, niet van TestWisely) stonden
              hier als keurmerk; zie updates/2026-10-18_publieke-teksten-kloppen.md. */}
          <div className="hh-partner">
            <img src="/entree-federatie-light.png" alt="Kennisnet Entree Federatie" className="hh-partner-entree" loading="lazy" />
          </div>
        </div>
        <div className="hh-partners-school">
          <span className="hh-partners-school-label">{t.partners.inGebruik}</span>
          <div className="hh-partners-school-logos">
            <img src="/lumion-logo-light.png" alt="Lumion Amsterdam" className="hh-partner-lumion" loading="lazy" />
            <img src="/fiducie-college-logo-white.png" alt="Fiducie College" className="hh-partner-fiducie" loading="lazy" />
          </div>
        </div>
      </section>

      {/* Waarom kiezen voor TestWisely? Section */}
      <section className="hh-waarom">

        <div className="hh-waarom-header">
          <h2 className="hh-waarom-title">
            {t.waarom.titel[0]}<br/>{t.waarom.titel[1]}
          </h2>
          <p className="hh-waarom-subtitle">
            {t.waarom.subtitel[0]}<br/>
            {t.waarom.subtitel[1]}
          </p>
        </div>

        <div className="hh-waarom-content">
          {/* Left: Dashboard mockup met statistieken en grafieken */}
          <div className="hh-waarom-illustration">
            <svg viewBox="20 40 440 490" fill="none" xmlns="http://www.w3.org/2000/svg">
              <defs>
                <linearGradient id="hh-dash-bg" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#f8fbff" />
                  <stop offset="100%" stopColor="#eef4fb" />
                </linearGradient>
                <linearGradient id="hh-dash-trend" x1="0" y1="1" x2="1" y2="0">
                  <stop offset="0%" stopColor="#0077b6" />
                  <stop offset="100%" stopColor="#00c7f5" />
                </linearGradient>
                <linearGradient id="hh-dash-trend-fill" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#0077b6" stopOpacity="0.32" />
                  <stop offset="100%" stopColor="#0077b6" stopOpacity="0" />
                </linearGradient>
                <linearGradient id="hh-dash-progress" x1="0" y1="0" x2="1" y2="0">
                  <stop offset="0%" stopColor="#0077b6" />
                  <stop offset="100%" stopColor="#10b981" />
                </linearGradient>
                <filter id="hh-dash-shadow" x="-10%" y="-10%" width="120%" height="120%">
                  <feDropShadow dx="0" dy="6" stdDeviation="10" floodColor="#0b1628" floodOpacity="0.12" />
                </filter>
              </defs>

              {/* Sparkles voor accent */}
              <path d="M40 30l4-12 4 12 12 4-12 4-4 12-4-12-12-4 12-4z" fill="#f59e0b" opacity="0.7"/>
              <path d="M440 60l3-9 3 9 9 3-9 3-3 9-3-9-9-3 9-3z" fill="#0077b6" opacity="0.5"/>
              <path d="M455 480l2-6 2 6 6 2-6 2-2 6-2-6-6-2 6-2z" fill="#10b981" opacity="0.6"/>

              {/* Browser/laptop window frame */}
              <g filter="url(#hh-dash-shadow)">
                <rect x="40" y="60" width="400" height="460" rx="18" fill="#ffffff" stroke="#e2e8f0" strokeWidth="1.5"/>

                {/* Window chrome (Mac-style traffic lights) */}
                <rect x="40" y="60" width="400" height="42" rx="18" fill="#f1f5f9"/>
                <rect x="40" y="86" width="400" height="16" fill="#f1f5f9"/>
                <circle cx="62" cy="81" r="6" fill="#fb7185"/>
                <circle cx="82" cy="81" r="6" fill="#fbbf24"/>
                <circle cx="102" cy="81" r="6" fill="#34d399"/>
                <rect x="180" y="73" width="120" height="16" rx="8" fill="#ffffff" stroke="#e2e8f0" strokeWidth="1"/>
                <text x="195" y="84" fontFamily="sans-serif" fontSize="9" fill="#94a3b8">testwisely.nl</text>

                {/* Sidebar */}
                <rect x="40" y="102" width="80" height="418" rx="0" fill="#0b1628"/>
                {/* TestWisely logo dots in sidebar */}
                <g transform="translate(70, 124)">
                  <circle cx="0"  cy="0"  r="3.5" fill="#00c7f5"/>
                  <circle cx="10" cy="0"  r="3.5" fill="#00c7f5"/>
                  <circle cx="20" cy="0"  r="3.5" fill="#00c7f5"/>
                  <circle cx="0"  cy="10" r="3.5" fill="#1e3a5f"/>
                  <circle cx="10" cy="10" r="3.5" fill="#7dd3fc"/>
                  <circle cx="20" cy="10" r="3.5" fill="#1e3a5f"/>
                  <circle cx="0"  cy="20" r="3.5" fill="#1e3a5f"/>
                  <circle cx="10" cy="20" r="3.5" fill="#1e3a5f"/>
                  <circle cx="20" cy="20" r="3.5" fill="#1e3a5f"/>
                </g>
                {/* Sidebar nav items */}
                <rect x="55" y="200" width="50" height="6" rx="3" fill="#334155"/>
                <rect x="55" y="232" width="50" height="6" rx="3" fill="#0077b6"/>
                <rect x="55" y="264" width="50" height="6" rx="3" fill="#334155"/>
                <rect x="55" y="296" width="50" height="6" rx="3" fill="#334155"/>
                <rect x="55" y="328" width="50" height="6" rx="3" fill="#334155"/>

                {/* Main content area */}
                <g transform="translate(140, 122)">
                  {/* Page title */}
                  <text x="0" y="14" fontFamily="Poppins, sans-serif" fontSize="14" fontWeight="700" fill="#0b1628">{t.waarom.dashboard.titel}</text>
                  <text x="0" y="32" fontFamily="sans-serif" fontSize="9" fill="#64748b">{t.waarom.dashboard.klas}</text>

                  {/* Top stat tile 1: 87% klas-gemiddelde */}
                  <g transform="translate(0, 50)">
                    <rect x="0" y="0" width="135" height="100" rx="12" fill="url(#hh-dash-bg)" stroke="#e2e8f0" strokeWidth="1"/>
                    <text x="14" y="22" fontFamily="sans-serif" fontSize="9" fontWeight="600" fill="#64748b">{t.waarom.dashboard.gemiddelde}</text>
                    <text x="14" y="62" fontFamily="Poppins, sans-serif" fontSize="34" fontWeight="800" fill="#0b1628">87%</text>
                    {/* Mini bullet trend up */}
                    <circle cx="106" cy="58" r="14" fill="#10b981"/>
                    <path d="M100 62 L106 54 L112 62" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
                    <line x1="106" y1="54" x2="106" y2="64" stroke="#fff" strokeWidth="2" strokeLinecap="round"/>
                    <text x="14" y="86" fontFamily="sans-serif" fontSize="9" fill="#10b981" fontWeight="600">{t.waarom.dashboard.verschil}</text>
                  </g>

                  {/* Top stat tile 2: trend chart */}
                  <g transform="translate(150, 50)">
                    <rect x="0" y="0" width="135" height="100" rx="12" fill="url(#hh-dash-bg)" stroke="#e2e8f0" strokeWidth="1"/>
                    <text x="14" y="22" fontFamily="sans-serif" fontSize="9" fontWeight="600" fill="#64748b">{t.waarom.dashboard.voortgang}</text>
                    {/* Mini line chart */}
                    <path d="M14,82 L34,74 L54,68 L74,58 L94,46 L114,32 L121,26"
                          stroke="url(#hh-dash-trend)" strokeWidth="2.5" fill="none" strokeLinecap="round"/>
                    <path d="M14,82 L34,74 L54,68 L74,58 L94,46 L114,32 L121,26 L121,90 L14,90 Z"
                          fill="url(#hh-dash-trend-fill)"/>
                    {/* Datapoints */}
                    <circle cx="34"  cy="74" r="2.5" fill="#fff" stroke="#0077b6" strokeWidth="1.5"/>
                    <circle cx="74"  cy="58" r="2.5" fill="#fff" stroke="#0077b6" strokeWidth="1.5"/>
                    <circle cx="121" cy="26" r="3.5" fill="#0077b6" stroke="#fff" strokeWidth="1.5"/>
                  </g>

                  {/* Bar chart per leerdoel */}
                  <g transform="translate(0, 168)">
                    <rect x="0" y="0" width="285" height="120" rx="12" fill="#ffffff" stroke="#e2e8f0" strokeWidth="1"/>
                    <text x="14" y="22" fontFamily="sans-serif" fontSize="9" fontWeight="600" fill="#64748b">{t.waarom.dashboard.leerdoel}</text>
                    {/* Bars */}
                    <g transform="translate(14, 36)">
                      {/* Y-axis baseline */}
                      <line x1="0" y1="68" x2="260" y2="68" stroke="#e2e8f0" strokeWidth="1"/>
                      {/* Bars */}
                      <rect x="0"   y="20" width="22" height="48" rx="3" fill="#0077b6"/>
                      <rect x="36"  y="8"  width="22" height="60" rx="3" fill="#0077b6"/>
                      <rect x="72"  y="32" width="22" height="36" rx="3" fill="#0077b6" opacity="0.65"/>
                      <rect x="108" y="14" width="22" height="54" rx="3" fill="#0077b6"/>
                      <rect x="144" y="2"  width="22" height="66" rx="3" fill="#10b981"/>
                      <rect x="180" y="22" width="22" height="46" rx="3" fill="#0077b6"/>
                      <rect x="216" y="10" width="22" height="58" rx="3" fill="#0077b6"/>
                      {/* Labels onder bars */}
                      <text x="11"  y="80" fontFamily="sans-serif" fontSize="7" fill="#94a3b8" textAnchor="middle">L1</text>
                      <text x="47"  y="80" fontFamily="sans-serif" fontSize="7" fill="#94a3b8" textAnchor="middle">L2</text>
                      <text x="83"  y="80" fontFamily="sans-serif" fontSize="7" fill="#94a3b8" textAnchor="middle">L3</text>
                      <text x="119" y="80" fontFamily="sans-serif" fontSize="7" fill="#94a3b8" textAnchor="middle">L4</text>
                      <text x="155" y="80" fontFamily="sans-serif" fontSize="7" fill="#94a3b8" textAnchor="middle">L5</text>
                      <text x="191" y="80" fontFamily="sans-serif" fontSize="7" fill="#94a3b8" textAnchor="middle">L6</text>
                      <text x="227" y="80" fontFamily="sans-serif" fontSize="7" fill="#94a3b8" textAnchor="middle">L7</text>
                    </g>
                  </g>

                  {/* Progress bar 78% */}
                  <g transform="translate(0, 306)">
                    <rect x="0" y="0" width="285" height="68" rx="12" fill="url(#hh-dash-bg)" stroke="#e2e8f0" strokeWidth="1"/>
                    <text x="14" y="20" fontFamily="sans-serif" fontSize="9" fontWeight="600" fill="#64748b">{t.waarom.dashboard.nagekeken}</text>
                    <text x="14" y="42" fontFamily="Poppins, sans-serif" fontSize="14" fontWeight="700" fill="#0b1628">{t.waarom.dashboard.voltooid}</text>
                    {/* Progress bar background */}
                    <rect x="14" y="50" width="257" height="8" rx="4" fill="#e2e8f0"/>
                    {/* Progress bar fill */}
                    <rect x="14" y="50" width="200" height="8" rx="4" fill="url(#hh-dash-progress)"/>
                  </g>
                </g>
              </g>
            </svg>
          </div>

          {/* Right: Benefits */}
          <div className="hh-waarom-benefits">
            <div className="hh-waarom-item">
              <div className="hh-waarom-check">
                <svg viewBox="0 0 24 24" fill="none">
                  <circle cx="12" cy="12" r="11" stroke="#0077b6" strokeWidth="1.5"/>
                  <path d="M7 12.5l3 3 7-7" stroke="#0077b6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <div>
                <h4 className="hh-waarom-name">{t.waarom.punten[0].naam}</h4>
                <p className="hh-waarom-text">{t.waarom.punten[0].tekst}</p>
              </div>
            </div>

            <div className="hh-waarom-item">
              <div className="hh-waarom-check">
                <svg viewBox="0 0 24 24" fill="none">
                  <circle cx="12" cy="12" r="11" stroke="#0077b6" strokeWidth="1.5"/>
                  <path d="M7 12.5l3 3 7-7" stroke="#0077b6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <div>
                <h4 className="hh-waarom-name">{t.waarom.punten[1].naam}</h4>
                <p className="hh-waarom-text">{t.waarom.punten[1].tekst}</p>
              </div>
            </div>

            <div className="hh-waarom-item">
              <div className="hh-waarom-check">
                <svg viewBox="0 0 24 24" fill="none">
                  <circle cx="12" cy="12" r="11" stroke="#0077b6" strokeWidth="1.5"/>
                  <path d="M7 12.5l3 3 7-7" stroke="#0077b6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <div>
                <h4 className="hh-waarom-name">{t.waarom.punten[2].naam}</h4>
                <p className="hh-waarom-text">{t.waarom.punten[2].tekst}</p>
              </div>
            </div>

            <div className="hh-waarom-item">
              <div className="hh-waarom-check">
                <svg viewBox="0 0 24 24" fill="none">
                  <circle cx="12" cy="12" r="11" stroke="#0077b6" strokeWidth="1.5"/>
                  <path d="M7 12.5l3 3 7-7" stroke="#0077b6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <div>
                <h4 className="hh-waarom-name">{t.waarom.punten[3].naam}</h4>
                <p className="hh-waarom-text">
                  {t.waarom.punten[3].tekst}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Extra benefits below, met opgaande wiskunde-grafiek rechts (diagonaal t.o.v. illustratie boven) */}
        <div className="hh-waarom-extra-wrapper">
        <div className="hh-waarom-extra">
          <div className="hh-waarom-item">
            <div className="hh-waarom-check">
              <svg viewBox="0 0 24 24" fill="none">
                <circle cx="12" cy="12" r="11" stroke="#0077b6" strokeWidth="1.5"/>
                <path d="M7 12.5l3 3 7-7" stroke="#0077b6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <div>
              <h4 className="hh-waarom-name">{t.waarom.luisteren.naam}</h4>
              <p className="hh-waarom-text">
                {t.waarom.luisteren.voor}<a href="mailto:info@testwisely.nl"><span className="hh-blue-underline">info@testwisely.nl</span></a>{t.waarom.luisteren.na}<strong>{t.waarom.luisteren.vet}</strong>{t.waarom.luisteren.slot}
              </p>
            </div>
          </div>

          <div className="hh-waarom-item">
            <div className="hh-waarom-check">
              <svg viewBox="0 0 24 24" fill="none">
                <circle cx="12" cy="12" r="11" stroke="#0077b6" strokeWidth="1.5"/>
                <path d="M7 12.5l3 3 7-7" stroke="#0077b6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <div>
              <h4 className="hh-waarom-name">{t.waarom.extra[0].naam}</h4>
              <p className="hh-waarom-text">{t.waarom.extra[0].tekst}</p>
            </div>
          </div>

          <div className="hh-waarom-item">
            <div className="hh-waarom-check">
              <svg viewBox="0 0 24 24" fill="none">
                <circle cx="12" cy="12" r="11" stroke="#0077b6" strokeWidth="1.5"/>
                <path d="M7 12.5l3 3 7-7" stroke="#0077b6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <div>
              <h4 className="hh-waarom-name">{t.waarom.extra[1].naam}</h4>
              <p className="hh-waarom-text">{t.waarom.extra[1].tekst}</p>
            </div>
          </div>

          <div className="hh-waarom-item">
            <div className="hh-waarom-check">
              <svg viewBox="0 0 24 24" fill="none">
                <circle cx="12" cy="12" r="11" stroke="#0077b6" strokeWidth="1.5"/>
                <path d="M7 12.5l3 3 7-7" stroke="#0077b6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <div>
              <h4 className="hh-waarom-name">{t.waarom.extra[2].naam}</h4>
              <p className="hh-waarom-text">{t.waarom.extra[2].tekst}</p>
            </div>
          </div>
        </div>

          {/* Opgaande wiskunde-grafiek, diagonaal tegenover de illustratie boven-links */}
          <div className="hh-waarom-graph" aria-hidden="true">
            <svg viewBox="0 0 420 340" fill="none" xmlns="http://www.w3.org/2000/svg">
              <defs>
                <linearGradient id="hh-graph-fill" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%"  stopColor="#0077b6" stopOpacity="0.35" />
                  <stop offset="100%" stopColor="#0077b6" stopOpacity="0" />
                </linearGradient>
                <linearGradient id="hh-graph-line" x1="0" y1="1" x2="1" y2="0">
                  <stop offset="0%"  stopColor="#0077b6" />
                  <stop offset="100%" stopColor="#00c7f5" />
                </linearGradient>
              </defs>

              {/* Grid */}
              <g stroke="#dfe7ee" strokeWidth="1">
                <line x1="50" y1="60"  x2="400" y2="60"  />
                <line x1="50" y1="125" x2="400" y2="125" />
                <line x1="50" y1="190" x2="400" y2="190" />
                <line x1="50" y1="255" x2="400" y2="255" />
                <line x1="50"  y1="60" x2="50"  y2="290" />
                <line x1="135" y1="60" x2="135" y2="290" />
                <line x1="220" y1="60" x2="220" y2="290" />
                <line x1="305" y1="60" x2="305" y2="290" />
                <line x1="390" y1="60" x2="390" y2="290" />
              </g>

              {/* X- en Y-as met arrows */}
              <g stroke="#1a1a2e" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="50" y1="290" x2="410" y2="290" />
                <line x1="50" y1="290" x2="50"  y2="40"  />
                <polyline points="405,284 415,290 405,296" fill="none" />
                <polyline points="44,50 50,40 56,50" fill="none" />
              </g>

              {/* Filled area onder de curve */}
              <path
                d="M50,275 C120,260 170,240 220,200 S320,90 395,55 L395,290 L50,290 Z"
                fill="url(#hh-graph-fill)"
              />

              {/* De curve zelf, boog die omhoog schiet */}
              <path
                d="M50,275 C120,260 170,240 220,200 S320,90 395,55"
                stroke="url(#hh-graph-line)"
                strokeWidth="4"
                strokeLinecap="round"
                fill="none"
              />

              {/* Datapunten */}
              <g>
                <circle cx="50"  cy="275" r="5" fill="#fff" stroke="#0077b6" strokeWidth="2.5" />
                <circle cx="135" cy="248" r="5" fill="#fff" stroke="#0077b6" strokeWidth="2.5" />
                <circle cx="220" cy="200" r="5" fill="#fff" stroke="#0077b6" strokeWidth="2.5" />
                <circle cx="305" cy="125" r="5" fill="#fff" stroke="#0077b6" strokeWidth="2.5" />
                <circle cx="395" cy="55"  r="7" fill="#0077b6" stroke="#fff" strokeWidth="3" />
              </g>

              {/* Trend-pijl op het einde */}
              <g transform="translate(360, 30)">
                <circle cx="20" cy="20" r="20" fill="#10b981" />
                <path d="M12 24 L20 14 L28 24" stroke="#fff" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" fill="none" />
                <line x1="20" y1="14" x2="20" y2="28" stroke="#fff" strokeWidth="3" strokeLinecap="round" />
              </g>

              {/* Sparkles voor extra accent */}
              <path d="M75 90l3-9 3 9 9 3-9 3-3 9-3-9-9-3 9-3z" fill="#f59e0b" opacity="0.7" />
              <path d="M345 200l2-6 2 6 6 2-6 2-2 6-2-6-6-2 6-2z" fill="#0077b6" opacity="0.6" />

              {/* Y-as labels */}
              <g fontFamily="sans-serif" fontSize="11" fill="#94a3b8">
                <text x="32" y="65"  textAnchor="end">100%</text>
                <text x="32" y="130" textAnchor="end">75%</text>
                <text x="32" y="195" textAnchor="end">50%</text>
                <text x="32" y="260" textAnchor="end">25%</text>
              </g>

              {/* Title-label */}
              <text x="60" y="30" fontFamily="Poppins, sans-serif" fontSize="14" fontWeight="700" fill="#1a1a2e">{t.waarom.grafiek}</text>
            </svg>
          </div>
        </div>
      </section>

      {/* Elk vak Section */}
      <section className="hh-vakken">
        <span className="hh-vakken-label">{t.vakken.label}</span>
        <h2 className="hh-vakken-title">
          <span className="hh-blue">{t.vakken.titel.nadruk}</span>{t.vakken.titel.na}
        </h2>
        <div className="hh-vakken-tags">
          {VAKKEN.map(({ sleutel, Icon, isFlag }) => (
            <span key={sleutel} className="hh-vak-tag">
              <span className="hh-vak-icon">
                {isFlag ? <Icon /> : <Icon size={30} strokeWidth={1.8} />}
              </span>
              {t.vakken.namen[sleutel]}
            </span>
          ))}
        </div>
        <div className="hh-vakken-tags">
          <span className="hh-vak-tag hh-vak-more">{t.vakken.meer}</span>
        </div>
        <nav className="hh-vak-links" aria-label={t.vakken.perVak}>
          <span className="hh-vak-links-label">{t.vakken.perVak}</span>
          <div className="hh-vak-links-rij">
            {VAK_PAGINAS.map((slug) => (
              <Link key={slug} to={vakPad(slug, lang)} className="hh-vak-link">
                {t.vakken.paginas[slug]}
                <PijlRechts />
              </Link>
            ))}
          </div>
        </nav>
      </section>

      {/* Uit de kennisbank: interne links naar de nieuwste artikelen */}
      <section className="hh-kennisbank" aria-labelledby="hh-kennisbank-titel">
        <div className="hh-kennisbank-inner">
          <h2 id="hh-kennisbank-titel" className="hh-kennisbank-titel">
            {t.kennisbank.titel.voor}<span className="hh-blue">{t.kennisbank.titel.nadruk}</span>
          </h2>
          <p className="hh-kennisbank-desc">
            {t.kennisbank.beschrijving}
          </p>
          {nieuwsteArtikelen.length > 0 && (
            <div className="hh-kennisbank-grid">
              {nieuwsteArtikelen.map((artikel) => (
                <KennisbankKaart key={artikel.slug} artikel={artikel} lang={lang} />
              ))}
            </div>
          )}
          <Link to={pad('kennisbank', lang)} className="hh-kennisbank-alle">
            {t.kennisbank.alle}
            <PijlRechts />
          </Link>
        </div>
      </section>

      {/* Reviews Section */}
      <section className="hh-reviews">
        <div className="hh-reviews-inner">
          <div className="hh-review-card">
            {/* Decorative circles inside card */}
            <div className="hh-review-circles">
              <div className="hh-rc"></div>
              <div className="hh-rc"></div>
              <div className="hh-rc"></div>
              <div className="hh-rc"></div>
              <div className="hh-rc"></div>
              <div className="hh-rc"></div>
            </div>

            <div className="hh-review-content">
              <div className="hh-review-quote-mark">&ldquo;</div>
              <p className="hh-review-text" key={reviewIndex}>
                {reviews[reviewIndex].quote}
              </p>
              <div className="hh-review-author">
                <span className="hh-review-name">{reviews[reviewIndex].name}</span>
                <span className="hh-review-role">{reviews[reviewIndex].role}</span>
              </div>
            </div>

            <div className="hh-review-avatar">
              <div
                className="hh-review-avatar-circle"
                style={{ background: reviews[reviewIndex].color }}
              >
                <span className="hh-review-initials">{reviews[reviewIndex].initials}</span>
              </div>
            </div>
          </div>

          {/* Slider dots */}
          <div className="hh-review-dots">
            {reviews.map((_, i) => (
              <button
                key={i}
                className={`hh-review-dot ${i === reviewIndex ? 'active' : ''}`}
                onClick={() => setReviewIndex(i)}
                aria-label={`${t.reviewLabel} ${i + 1}`}
              />
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <PubliekeFooter lang={lang} />
    </div>
  );
}

export default HomeHero;
